
import { TriggerNode } from '@/components/automation/nodes/TriggerNode';
import { MessageNode } from '@/components/automation/nodes/MessageNode';
import { ConditionNode } from '@/components/automation/nodes/ConditionNode';
import { AIAgentNode } from '@/components/automation/nodes/AIAgentNode';
import { DelayNode } from '@/components/automation/nodes/DelayNode';
import { 
  MessageSquare, 
  GitBranch,
  Clock, 
  BrainCircuit, 
  Zap, 
  Globe, 
  User, 
  Send,
  Bot
} from 'lucide-react';
import { NodeTypeConfig, NodeType } from '@/types/automation';

// Define node type configurations
export const nodeTypes = {
  trigger: TriggerNode,
  message: MessageNode,
  condition: ConditionNode,
  aiAgent: AIAgentNode,
  delay: DelayNode,
};

// Node type configurations for the sidebar panel
export const nodeTypeConfigurations: NodeTypeConfig[] = [
  {
    type: 'trigger',
    label: 'Gatilho',
    description: 'Inicia um fluxo de automação',
    icon: <Zap className="h-5 w-5" />,
    color: 'bg-green-100 text-green-600',
    ports: { input: 0, output: 1 },
    configFields: [
      { 
        name: 'triggerType', 
        type: 'select', 
        label: 'Tipo de Gatilho',
        options: [
          { label: 'Mensagem recebida', value: 'message_received' },
          { label: 'Horário específico', value: 'schedule' },
          { label: 'Contato adicionado', value: 'contact_added' },
          { label: 'Campanha iniciada', value: 'campaign_started' },
        ],
        required: true
      }
    ]
  },
  {
    type: 'message',
    label: 'Mensagem',
    description: 'Envia uma mensagem para o contato',
    icon: <MessageSquare className="h-5 w-5" />,
    color: 'bg-blue-100 text-blue-600',
    ports: { input: 1, output: 1 },
    configFields: [
      { 
        name: 'message', 
        type: 'textarea', 
        label: 'Mensagem',
        placeholder: 'Digite a mensagem a ser enviada...',
        required: true
      },
      { 
        name: 'channel', 
        type: 'select', 
        label: 'Canal',
        options: [
          { label: 'WhatsApp', value: 'whatsapp' },
          { label: 'Telegram', value: 'telegram' },
          { label: 'Ambos', value: 'both' },
        ],
        default: 'whatsapp'
      }
    ]
  },
  {
    type: 'condition',
    label: 'Condição',
    description: 'Bifurca o fluxo com base em uma condição',
    icon: <GitBranch className="h-5 w-5" />,
    color: 'bg-yellow-100 text-yellow-600',
    ports: { input: 1, output: 2 },
    configFields: [
      { 
        name: 'condition', 
        type: 'select', 
        label: 'Condição',
        options: [
          { label: 'Contém palavra-chave', value: 'contains_keyword' },
          { label: 'Sentimento positivo', value: 'positive_sentiment' },
          { label: 'Sentimento negativo', value: 'negative_sentiment' },
          { label: 'Horário comercial', value: 'business_hours' },
        ],
        required: true
      },
      { 
        name: 'keyword', 
        type: 'text', 
        label: 'Palavra-chave',
        placeholder: 'Ex: atendimento',
        required: false
      }
    ]
  },
  {
    type: 'delay',
    label: 'Atraso',
    description: 'Adiciona um atraso antes da próxima ação',
    icon: <Clock className="h-5 w-5" />,
    color: 'bg-orange-100 text-orange-600',
    ports: { input: 1, output: 1 },
    configFields: [
      { 
        name: 'delay', 
        type: 'number', 
        label: 'Duração',
        default: 5,
        required: true
      },
      { 
        name: 'unit', 
        type: 'select', 
        label: 'Unidade',
        options: [
          { label: 'Minutos', value: 'minutes' },
          { label: 'Horas', value: 'hours' },
          { label: 'Dias', value: 'days' },
        ],
        default: 'minutes'
      }
    ]
  },
  {
    type: 'aiAgent',
    label: 'Agente IA',
    description: 'Processa a mensagem com um agente de IA',
    icon: <BrainCircuit className="h-5 w-5" />,
    color: 'bg-purple-100 text-purple-600',
    ports: { input: 1, output: 1 },
    configFields: [
      { 
        name: 'agentId', 
        type: 'select', 
        label: 'Agente de IA',
        options: [], // Will be populated dynamically
        required: true
      },
      { 
        name: 'prompt', 
        type: 'textarea', 
        label: 'Prompt Adicional',
        placeholder: 'Instruções adicionais para o agente...',
      }
    ]
  },
  {
    type: 'webhook',
    label: 'Webhook',
    description: 'Envia dados para um serviço externo',
    icon: <Globe className="h-5 w-5" />,
    color: 'bg-indigo-100 text-indigo-600',
    ports: { input: 1, output: 1 },
    configFields: [
      { 
        name: 'url', 
        type: 'text', 
        label: 'URL do Webhook',
        placeholder: 'https://...',
        required: true
      },
      { 
        name: 'method', 
        type: 'select', 
        label: 'Método',
        options: [
          { label: 'POST', value: 'post' },
          { label: 'GET', value: 'get' },
          { label: 'PUT', value: 'put' },
        ],
        default: 'post'
      }
    ]
  },
  {
    type: 'contact',
    label: 'Contato',
    description: 'Atualiza ou adiciona informações do contato',
    icon: <User className="h-5 w-5" />,
    color: 'bg-pink-100 text-pink-600',
    ports: { input: 1, output: 1 },
    configFields: [
      { 
        name: 'action', 
        type: 'select', 
        label: 'Ação',
        options: [
          { label: 'Adicionar tag', value: 'add_tag' },
          { label: 'Atualizar campo', value: 'update_field' },
          { label: 'Adicionar a grupo', value: 'add_to_group' },
        ],
        required: true
      },
      { 
        name: 'value', 
        type: 'text', 
        label: 'Valor',
        placeholder: 'Nome da tag ou valor do campo',
        required: true
      }
    ]
  },
  {
    type: 'quickReply',
    label: 'Resposta Rápida',
    description: 'Adiciona botões de resposta rápida',
    icon: <Send className="h-5 w-5" />,
    color: 'bg-cyan-100 text-cyan-600',
    ports: { input: 1, output: 1 },
    configFields: [
      { 
        name: 'question', 
        type: 'text', 
        label: 'Pergunta',
        placeholder: 'Selecione uma opção:',
        required: true
      },
      { 
        name: 'options', 
        type: 'textarea', 
        label: 'Opções (uma por linha)',
        placeholder: 'Sim\nNão\nTalvez',
        required: true
      }
    ]
  },
  {
    type: 'campaign',
    label: 'Campanha',
    description: 'Inicia ou agenda uma campanha',
    icon: <Zap className="h-5 w-5" />,
    color: 'bg-amber-100 text-amber-600',
    ports: { input: 1, output: 1 },
    configFields: [
      { 
        name: 'campaignId', 
        type: 'select', 
        label: 'Campanha',
        options: [], // Will be populated dynamically
        required: true
      },
      { 
        name: 'action', 
        type: 'select', 
        label: 'Ação',
        options: [
          { label: 'Iniciar agora', value: 'start_now' },
          { label: 'Agendar', value: 'schedule' },
        ],
        default: 'start_now'
      }
    ]
  },
];

export default nodeTypes;
